import * as React from "react";
import { Copy, Download, FileJson, FileText, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  createExportLink,
  type ExportLink,
  type ExportLinkFormat,
} from "@/lib/api";

type FormatOption = {
  id: ExportLinkFormat;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};

const FORMATS: FormatOption[] = [
  {
    id: "markdown",
    label: "Markdown transcript",
    description:
      "Full Q&A with inline [Citation Tags] and a sources table. Best for sharing with a colleague or archiving.",
    icon: FileText,
  },
  {
    id: "json",
    label: "JSON for AI agent",
    description:
      "Structured payload with messages, cited tags, and source metadata. Best for downstream ingestion by an LP-side AI agent.",
    icon: FileJson,
  },
];

function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function ExportDialog({
  conversationId,
  open,
  onClose,
}: {
  conversationId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [link, setLink] = React.useState<ExportLink | null>(null);
  const [busy, setBusy] = React.useState<ExportLinkFormat | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [copyOk, setCopyOk] = React.useState(false);
  const [now, setNow] = React.useState<number>(() => Date.now());

  // Reset state when dialog closes/opens.
  React.useEffect(() => {
    if (!open) {
      setLink(null);
      setBusy(null);
      setError(null);
      setCopyOk(false);
    }
  }, [open]);

  // Tick once a second so the countdown re-renders.
  React.useEffect(() => {
    if (!link) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [link]);

  // Escape to close.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !conversationId) return null;

  async function pickFormat(format: ExportLinkFormat) {
    if (!conversationId) return;
    setBusy(format);
    setError(null);
    setLink(null);
    try {
      const res = await createExportLink(conversationId, format);
      setLink(res);
      setNow(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate link");
    } finally {
      setBusy(null);
    }
  }

  async function handleCopy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch {
      /* silent */
    }
  }

  const remainingMs = link
    ? new Date(link.expires_at).getTime() - now
    : 0;
  const expired = link != null && remainingMs <= 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-md border bg-card p-5 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Export conversation</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Single-use download link, valid for 5 minutes. The transcript honors
              the same redaction rules as live chat — per-recipient compensation,
              individual grant unit counts, and litigation dollar exposure are not
              included.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close export dialog"
            onClick={onClose}
          >
            <X />
          </Button>
        </div>

        {!link ? (
          <div className="mt-4 grid gap-2">
            {FORMATS.map((f) => {
              const Icon = f.icon;
              const isBusy = busy === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  disabled={busy !== null}
                  onClick={() => pickFormat(f.id)}
                  className="flex w-full items-start gap-3 rounded-md border bg-background p-3 text-left transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon className="mt-0.5 size-5 shrink-0 text-brand" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      {f.label}
                      {isBusy ? (
                        <Badge variant="outline" className="text-[10px]">
                          Generating…
                        </Badge>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {f.description}
                    </p>
                  </div>
                </button>
              );
            })}
            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                {error}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border border-success/30 bg-success-soft p-3">
              <div className="text-xs font-semibold text-success">
                {link.format === "json" ? "JSON" : "Markdown"} link ready
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Single-use. Expires in{" "}
                <span
                  className={
                    expired ? "font-mono text-destructive" : "font-mono"
                  }
                >
                  {expired ? "expired" : formatCountdown(remainingMs)}
                </span>
                . Open it in a new tab to download, or paste the URL into your
                agent.
              </div>
            </div>
            <div className="flex gap-2">
              <input
                value={link.url}
                readOnly
                className="flex-1 rounded-md border bg-background px-3 py-2 text-xs font-mono text-foreground"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={expired}
              >
                <Copy />
                {copyOk ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => window.open(link.url, "_blank", "noopener")}
                disabled={expired}
                className="flex-1"
              >
                <Download />
                {expired ? "Link expired" : `Open ${link.format === "json" ? "JSON" : "Markdown"}`}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setLink(null);
                  setError(null);
                }}
              >
                Choose another format
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Tip: most AI agents accept a URL fetcher tool — paste the URL
              above into the agent's HTTP fetch within the 5-minute window to
              ingest the full transcript.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
